import { Alert, Card, Col, Empty, Row, Statistic } from "antd";
import { useEffect, useState } from "react";

import request from "@/api";
import { MyTaskAction } from "./MyTaskAction";

export interface MyTaskTradeStat {
    key: string;
    label: string;
    state: string;
    count: number;
    permissions?: readonly string[];
}

interface MyTaskTradeStatsProps {
    loadStats?: () => Promise<MyTaskTradeStat[]>;
}

const getCount = (value: unknown, field: "toBeReviewed" | "toBeConfirmed") => {
    const count = (value as Record<string, unknown> | undefined)?.[field];
    return typeof count === "number" ? count : Number(count || 0);
};

/** 复用 Vue 商品交易待办的六个统计接口和状态文案。 */
export async function loadMyTaskTradeStats(): Promise<MyTaskTradeStat[]> {
    const results = await Promise.all([
        request.service.get("/admin/mytask/statisc/product-consignment"),
        request.service.get("/admin/mytask/statisc/product-group-purchase"),
        request.service.get("/admin/mytask/statisc/product-bargain"),
        request.service.get("/mytask/statisc/receive-confirm"),
        request.service.get("/admin/mytask/statisc/sample-apply"),
        request.service.get("/admin/mytask/statisc/refund")
    ]);
    const definitions = [
        {
            key: "consignment",
            label: "寄售商品管理",
            state: "待审核",
            field: "toBeReviewed",
            permissions: ["ROLE_PRODUCT_MANAGE"]
        },
        {
            key: "groupPurchase",
            label: "团购商品管理",
            state: "待审核",
            field: "toBeReviewed",
            permissions: ["ROLE_PRODUCT_MANAGE"]
        },
        {
            key: "bargain",
            label: "捡漏商品管理",
            state: "待审核",
            field: "toBeReviewed",
            permissions: ["ROLE_PRODUCT_MANAGE"]
        },
        {
            key: "collection",
            label: "收款管理",
            state: "待确认支付",
            field: "toBeConfirmed",
            permissions: ["ROLE_PRODUCT_MANAGE"]
        },
        {
            key: "sample",
            label: "申请寄样",
            state: "待处理",
            field: "toBeReviewed",
            permissions: ["ROLE_APPLY_SAMPLE_DEAL"]
        },
        {
            key: "refund",
            label: "退款管理",
            state: "待审核",
            field: "toBeReviewed",
            permissions: ["ROLE_REFUND_MANAGE"]
        }
    ] as const;
    return definitions.map((definition, index) => ({
        key: definition.key,
        label: definition.label,
        state: definition.state,
        count: getCount(results[index]?.data, definition.field),
        permissions: definition.permissions
    }));
}

/** 商品交易待办统计卡片，沿用 Vue 的六类数量和状态展示。 */
export function MyTaskTradeStats({ loadStats = loadMyTaskTradeStats }: MyTaskTradeStatsProps) {
    const [stats, setStats] = useState<MyTaskTradeStat[]>([]);
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        let disposed = false;
        void loadStats()
            .then(result => {
                if (disposed) return;
                setStats(result);
                setErrorMessage("");
            })
            .catch(() => {
                if (!disposed) setErrorMessage("商品交易待办统计加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [loadStats]);

    return (
        <Card title="商品交易">
            {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
            {stats.length ? (
                <Row gutter={[16, 16]}>
                    {stats.map(stat => (
                        <Col key={stat.key} xs={24} sm={12} md={8}>
                            <MyTaskAction stat={stat}>
                                <div>
                                    <Statistic title={stat.label} value={stat.count} />
                                    <span>{stat.state}</span>
                                </div>
                            </MyTaskAction>
                        </Col>
                    ))}
                </Row>
            ) : (
                <Empty description="暂无数据" />
            )}
        </Card>
    );
}
